// Drawola Studio: whether the visitor already pressed the welcome CTA.
// Fork-only.
//
// The root page shows the welcome until the visitor presses Continue. That
// click is stored in its own host-only cookie for 90 days, so the head gate in
// index.html can read it before first paint. Only the click writes it: reads
// never renew it, so the welcome comes back 90 days after the last click.
// The has-draws hint in `prefs.ts` is a separate thing and never decides this.

export const WELCOME_ACK_COOKIE = "drawola-studio-welcome-ack";
export const WELCOME_ACK_VALUE = "1";
/** 90 days, in seconds. */
export const WELCOME_ACK_MAX_AGE_SECONDS = 7776000;

const ACK_PAIR = `${WELCOME_ACK_COOKIE}=${WELCOME_ACK_VALUE}`;

/**
 * Whether a `document.cookie` string holds the exact acknowledgment pair.
 * The head gate in index.html parses the same way; keep both in step.
 */
export const hasWelcomeAckCookie = (cookies: string): boolean =>
  cookies.split(";").some((pair) => pair.trim() === ACK_PAIR);

/** The `document.cookie` assignment that stores the acknowledgment. */
export const buildWelcomeAckCookie = (secure: boolean): string =>
  `${ACK_PAIR}; Max-Age=${WELCOME_ACK_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${
    secure ? "; Secure" : ""
  }`;

/** Cookie access the acknowledgment needs; any member may throw. */
export type CookieJar = {
  read: () => string;
  write: (cookie: string) => void;
  isSecure: () => boolean;
};

const documentCookieJar: CookieJar = {
  read: () => document.cookie,
  write: (cookie) => {
    document.cookie = cookie;
  },
  isSecure: () => window.location.protocol === "https:",
};

/**
 * The visitor's welcome acknowledgment: the stored cookie, plus an in-memory
 * flag so a click still counts for this page when the cookie is blocked or
 * silently dropped. The flag dies with the page, so it never hides an expiry.
 */
export class WelcomeAcknowledgment {
  private acknowledgedOnThisPage = false;
  private readFailureLogged = false;
  private writeFailureLogged = false;

  /** Uses the given cookie access, the real `document.cookie` by default. */
  constructor(private readonly jar: CookieJar = documentCookieJar) {}

  /** Whether the welcome was acknowledged; never writes or renews the cookie. */
  isAcknowledged(): boolean {
    if (this.acknowledgedOnThisPage) {
      return true;
    }
    try {
      return hasWelcomeAckCookie(this.jar.read());
    } catch (error) {
      // logged once: Studio reads this on every mount
      if (!this.readFailureLogged) {
        this.readFailureLogged = true;
        console.warn("[drawOla] welcome: reading the cookie failed", error);
      }
      return false;
    }
  }

  /** Records a real CTA click: for this page always, for 90 days when allowed. */
  acknowledge(): void {
    this.acknowledgedOnThisPage = true;
    try {
      this.jar.write(buildWelcomeAckCookie(this.jar.isSecure()));
    } catch (error) {
      // blocked cookies only cost the next page load, not this one
      if (!this.writeFailureLogged) {
        this.writeFailureLogged = true;
        console.warn("[drawOla] welcome: storing the cookie failed", error);
      }
    }
  }
}

/** The page-wide instance, shared by every Studio mount on this page. */
export const welcomeAcknowledgment = new WelcomeAcknowledgment();
